/* CapWorld: collection-only, mobile main/last-photo enhancement. */
(() => {
  'use strict';
  const TAG = 'cw-collection-swipe';
  if (!window.customElements || !window.PointerEvent || !window.AbortController || customElements.get(TAG)) return;
  const mobile = matchMedia('(max-width: 767px)');
  const cardSelector = '#MainContent [data-section-type="collection-template"][data-cw-collection-swipe] .collection__products > .grid > .product-grid-item';

  customElements.define(TAG, class extends HTMLElement {
    connectedCallback() {
      this.life?.abort();
      this.life = new AbortController();
      this.refresh = () => this.schedule();
      mobile.addEventListener('change', this.refresh, {signal: this.life.signal});
      window.addEventListener('load', this.refresh, {once: true, signal: this.life.signal});
      document.querySelectorAll('link[href*="cw-collection-swipe.css"]').forEach(link => {
        link.addEventListener('load', this.refresh, {signal: this.life.signal});
      });
      this.schedule();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.life?.abort();
      this.teardown();
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.mount());
    }

    mount() {
      if (!this.isConnected || !mobile.matches) return this.teardown();
      if (this.cycleController) return;
      const card = this.parentElement;
      if (!card?.matches(cardSelector)) return;
      if (getComputedStyle(this).getPropertyValue('--cw-swipe-css-ready').trim() !== '1') return;
      const imageBox = card.querySelector(':scope > .product-grid-item__image');
      const link = imageBox?.querySelector('.product__media__container > a.product__media__holder[data-grid-link]');
      const main = link?.querySelector(':scope > [data-product-media-featured]');
      const template = this.querySelector('template[data-cw-last-template]');
      const original = template?.content.querySelector('img[data-cw-last-photo]');
      const gap = imageBox ? Math.max(0, parseFloat(getComputedStyle(imageBox).marginBottom) || 0) : 0;
      if (!link || !main || !original || link.hasAttribute('data-cw-swipe-enabled')) return;

      this.link = link;
      this.main = main;
      this.originalMainAria = main.getAttribute('aria-hidden');
      this.index = 0;
      this.ready = false;
      this.blockClick = false;
      this.cycleController = new AbortController();
      this.style.setProperty('--cw-swipe-gap', `${gap}px`);
      const cycle = this.cycleController;
      const load = () => {
        if (!this.isConnected || !mobile.matches || this.cycleController !== cycle) return;
        this.intersection?.disconnect();
        this.intersection = null;
        this.loadLast(original, cycle);
      };
      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) load();
        }, {rootMargin: '300px 0px'});
        this.intersection.observe(imageBox);
      } else {
        load();
      }
    }

    loadLast(original, cycle) {
      if (this.lastLayer) return;
      const layer = document.createElement('span');
      layer.className = 'cw-swipe-last';
      layer.hidden = true;
      layer.setAttribute('aria-hidden', 'true');
      layer.style.setProperty('--cw-swipe-fit', this.dataset.cwFit === 'cover' ? 'cover' : 'contain');
      const image = original.cloneNode(true);
      image.loading = 'eager';
      image.decoding = 'async';
      image.draggable = false;
      this.lastLayer = layer;
      const valid = () => this.isConnected && mobile.matches && this.cycleController === cycle;
      const fail = () => { if (valid()) this.teardown(); };
      let decoding = false;
      const loaded = () => {
        if (!valid() || decoding || !image.naturalWidth) return;
        decoding = true;
        const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
        decoded.then(() => {
          if (valid() && image.naturalWidth) this.activate();
        }).catch(fail);
      };
      image.addEventListener('load', loaded, {signal: cycle.signal});
      image.addEventListener('error', fail, {signal: cycle.signal});
      layer.append(image);
      this.link.append(layer);
      if (image.complete) {
        if (image.naturalWidth) loaded();
        else fail();
      }
    }

    activate() {
      if (this.ready || !mobile.matches || !this.lastLayer) return;
      this.ready = true;
      this.link.setAttribute('data-cw-swipe-enabled', '');
      this.lastLayer.hidden = false;
      this.hidden = false;
      this.setIndex(0, false);
      const signal = this.cycleController.signal;
      this.link.addEventListener('pointerdown', event => this.down(event), {passive: true, signal});
      this.link.addEventListener('pointermove', event => this.move(event), {passive: false, signal});
      this.link.addEventListener('pointerup', event => this.up(event), {passive: true, signal});
      this.link.addEventListener('pointercancel', () => this.cancelGesture(true), {passive: true, signal});
      this.link.addEventListener('lostpointercapture', () => {
        if (this.gesture) this.cancelGesture(true);
      }, {passive: true, signal});
      this.link.addEventListener('dragstart', event => event.preventDefault(), {signal});
      this.link.addEventListener('click', event => {
        // Keyboard activation has detail=0. A fresh pointerdown clears this flag.
        if (mobile.matches && this.blockClick && event.detail !== 0) {
          this.blockClick = false;
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, {capture: true, signal});
      this.link.addEventListener('keydown', event => {
        if (!mobile.matches || event.target !== this.link || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.cancelGesture(false);
        this.setIndex(event.key === 'ArrowRight' ? 1 : 0);
      }, {signal});
      this.querySelectorAll('[data-cw-dot]').forEach(button => {
        button.addEventListener('click', event => {
          if (!mobile.matches || !this.ready) return;
          event.preventDefault();
          event.stopPropagation();
          this.cancelGesture(false);
          this.setIndex(Number(button.dataset.cwDot));
        }, {signal});
      });
      this.primary = this.currentPrimary();
      this.variantObserver = new MutationObserver(records => {
        const primary = this.currentPrimary();
        if (primary !== this.primary || records.some(record => record.target === this.link && record.attributeName === 'href')) {
          this.primary = primary;
          this.cancelGesture(false);
          this.setIndex(0, false);
        }
      });
      this.variantObserver.observe(this.main, {subtree: true, childList: true, attributes: true, attributeFilter: ['class']});
      this.variantObserver.observe(this.link, {attributes: true, attributeFilter: ['href']});
    }

    currentPrimary() {
      return this.main?.querySelector('.product__media--featured-visible:not(.product__media--featured-secondary)');
    }

    down(event) {
      if (!this.ready || !mobile.matches) return;
      if (!event.isPrimary) return this.cancelGesture(true);
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.cancelGesture(false);
      this.blockClick = false;
      this.gesture = {
        id: event.pointerId, x: event.clientX, y: event.clientY,
        width: this.link.getBoundingClientRect().width, start: this.index,
        dx: 0, dy: 0, distance: 0, axis: null
      };
      try { this.link.setPointerCapture(event.pointerId); } catch (_) { /* Detached/cancelled pointer. */ }
    }

    measure(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return null;
      gesture.dx = event.clientX - gesture.x;
      gesture.dy = event.clientY - gesture.y;
      gesture.distance = Math.max(gesture.distance, Math.hypot(gesture.dx, gesture.dy));
      if (!gesture.axis && Math.max(Math.abs(gesture.dx), Math.abs(gesture.dy)) >= 8) {
        gesture.axis = Math.abs(gesture.dx) > Math.abs(gesture.dy) * 1.2 ? 'x' : 'y';
      }
      return gesture;
    }

    move(event) {
      if (!mobile.matches) return this.cancelGesture(false);
      const gesture = this.measure(event);
      if (!gesture || gesture.axis !== 'x') return;
      if (event.cancelable) event.preventDefault();
      this.link.setAttribute('data-cw-swipe-dragging', '');
      const offset = Math.max(-gesture.width, Math.min(0, -gesture.start * gesture.width + gesture.dx));
      this.link.style.setProperty('--cw-swipe-offset', `${offset}px`);
    }

    up(event) {
      if (!mobile.matches) return this.cancelGesture(false);
      const gesture = this.measure(event);
      if (!gesture) return;
      let next = gesture.start;
      if (gesture.axis === 'x' && Math.abs(gesture.dx) >= Math.max(24, gesture.width * 0.15)) {
        next = gesture.dx < 0 ? 1 : 0;
      }
      this.blockClick = gesture.distance >= 8;
      this.gesture = null;
      this.release(gesture.id);
      this.setIndex(next);
    }

    release(id) {
      try {
        if (this.link?.hasPointerCapture(id)) this.link.releasePointerCapture(id);
      } catch (_) { /* Browser already released the pointer. */ }
    }

    cancelGesture(blockClick) {
      const gesture = this.gesture;
      if (!gesture) return;
      this.gesture = null;
      if (blockClick) this.blockClick = true;
      this.release(gesture.id);
      if (this.ready) this.setIndex(gesture.start, false);
    }

    setIndex(index, announce = true) {
      if (!this.ready) return;
      this.index = index === 1 ? 1 : 0;
      this.link.removeAttribute('data-cw-swipe-dragging');
      this.link.style.setProperty('--cw-swipe-offset', this.index ? '-100%' : '0%');
      this.link.setAttribute('data-cw-swipe-index', String(this.index));
      this.setAttribute('data-cw-swipe-index', String(this.index));
      this.querySelectorAll('[data-cw-dot]').forEach(dot => {
        const active = Number(dot.dataset.cwDot) === this.index;
        dot.toggleAttribute('data-cw-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });
      if (this.index) this.main.setAttribute('aria-hidden', 'true');
      else this.restoreMainAria();
      this.lastLayer.setAttribute('aria-hidden', this.index ? 'false' : 'true');
      const status = this.querySelector('[data-cw-swipe-status]');
      if (status) status.textContent = announce ? `Photo ${this.index + 1} of 2` : '';
    }

    restoreMainAria() {
      if (!this.main) return;
      if (this.originalMainAria === null) this.main.removeAttribute('aria-hidden');
      else if (this.originalMainAria !== undefined) this.main.setAttribute('aria-hidden', this.originalMainAria);
    }

    teardown() {
      this.cancelGesture(false);
      this.cycleController?.abort();
      this.cycleController = null;
      this.intersection?.disconnect();
      this.intersection = null;
      this.variantObserver?.disconnect();
      this.variantObserver = null;
      this.hidden = true;
      this.ready = false;
      this.blockClick = false;
      this.restoreMainAria();
      if (this.link) {
        this.link.removeAttribute('data-cw-swipe-enabled');
        this.link.removeAttribute('data-cw-swipe-index');
        this.link.removeAttribute('data-cw-swipe-dragging');
        this.link.style.removeProperty('--cw-swipe-offset');
      }
      this.lastLayer?.remove();
      this.lastLayer = null;
      this.removeAttribute('data-cw-swipe-index');
      this.style.removeProperty('--cw-swipe-gap');
      this.querySelectorAll('[data-cw-dot]').forEach(dot => {
        dot.removeAttribute('data-cw-active');
        dot.setAttribute('aria-pressed', String(Number(dot.dataset.cwDot) === 0));
      });
      const status = this.querySelector('[data-cw-swipe-status]');
      if (status) status.textContent = '';
      this.link = null;
      this.main = null;
      this.primary = null;
    }
  });
})();
